/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Query } from 'react-apollo';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Button, Grid, Hidden, Typography, breakpoints } from '@coursera/cds-core';
import { Card } from '@coursera/coursera-ui';

import TrackedButton from 'bundles/page/components/TrackedButton';

import bannerBackgroundImage from 'images/degree-home/banner-background.svg';
import calendarImage from 'images/degree-home/calendar.png';

import _t from 'i18n!nls/degree-home';

import { useCoursesContext } from '../CoursesProvider';
import type {
  GetUpcomingCourseStatesQuery_DegreeLearnerCourseStatesV1Resource_get_notStarted_courseProgressState_DegreeLearnerCourseStatesV1_notStartedMember as CourseProgressState,
  GetUpcomingCourseStatesQuery_DegreeLearnerCourseStatesV1Resource_get_notStarted as NotStartedCourse,
} from '../UpcomingCourses/__generated__/getUpcomingCourseStatesQuery';
import EnrollmentReminderBannerDescription from './EnrollmentReminderBannerDescription';
import { getEnrollableSessionsQuery } from './EnrollmentReminderQueries';
import type {
  GetEnrollableSessionsQuery_OnDemandSessionsV1Resource_nextEnrollableSessionsByUserAndCourse_elements as EnrollableSession,
  GetEnrollableSessionsQuery,
  GetEnrollableSessionsQueryVariables,
} from './__generated__/GetEnrollableSessionsQuery';

const styles = {
  contentContainer: () => css`
    background-image: url(${bannerBackgroundImage});
    background-position: right;
    background-repeat: no-repeat;
    background-size: contain;
    padding: var(--cds-spacing-600) 40px;

    ${breakpoints.down('xs')} {
      background-size: cover;
    }
  `,
  bannerHeading: () => css`
    margin-bottom: var(--cds-spacing-100);
  `,
  enrollBtn: () => css`
    margin-top: var(--cds-spacing-300);
  `,
  bottomSpace: css`
    margin-bottom: 1.5rem;
  `,
  imgGrid: css`
    display: flex;
    justify-content: right;
    align-items: center;
  `,
};

// Filters 'notStarted' courses to find items where there is no active session, and there are sessions availble to enroll in
const enrollableCoursesFilterFn = (notStartedCourse: NotStartedCourse) => {
  const progressState = notStartedCourse.courseProgressState as unknown as CourseProgressState;
  return !progressState.notStarted.activeSessionId && progressState.notStarted.hasEnrollableSession;
};

type EnrollmentReminderBannerContentProps = {
  enrollableCourses?: NotStartedCourse[];
  singleEnrollableCourse?: EnrollableSession | null;
};

export const EnrollmentReminderBannerContent = ({
  enrollableCourses,
  singleEnrollableCourse,
}: EnrollmentReminderBannerContentProps) => {
  const { setShowEnrollmentDialog } = useCoursesContext();
  const router = useRouter();

  const handleEnrollClick = () => {
    router.push({
      ...router.location,
      params: router.params,
      query: {
        ...router.location.query,
        showEnrollmentDialog: true,
      },
    });

    setShowEnrollmentDialog(true);
  };

  if (!enrollableCourses || enrollableCourses.length < 1) return null;

  return (
    <Card
      isCardBlock={true}
      htmlAttributes={{ 'data-e2e': 'enrollment-reminder-banner' }}
      aria-label={_t('Enrollment reminder')}
      style={{ marginBottom: '1.5rem', backgroundColor: 'var(--cds-color-blue-25)', boxShadow: 'none' }}
    >
      <Grid container alignContent="center" css={styles.contentContainer}>
        <Grid item xs={12}>
          <Hidden smUp>
            <img src={calendarImage} alt={_t('Calendar icon')} width="74" css={styles.bottomSpace} />
          </Hidden>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Typography variant="h2semibold" color="body" css={styles.bannerHeading}>
            {_t('Jump into your upcoming courses')}
          </Typography>
          <EnrollmentReminderBannerDescription
            enrollableCourses={enrollableCourses}
            singleEnrollableCourse={singleEnrollableCourse}
          />
          <Button
            css={styles.enrollBtn}
            variant="primary"
            size="small"
            onClick={handleEnrollClick}
            component={TrackedButton}
            trackingName="reminder_banner_start_enrollment"
            withVisibilityTracking={false}
            requireFullyVisible={false}
          >
            {_t('Start enrollment')}
          </Button>
        </Grid>
        <Grid item xs={12} sm={3} css={styles.imgGrid}>
          <Hidden xsDown>
            <img src={calendarImage} alt={_t('calendar icon')} width="192" />
          </Hidden>
        </Grid>
      </Grid>
    </Card>
  );
};

export const EnrollmentReminderBanner = () => {
  const { upcomingCoursesData } = useCoursesContext();
  const enrollableCourses = upcomingCoursesData?.filter(enrollableCoursesFilterFn);

  return (
    <React.Fragment>
      <Query<GetEnrollableSessionsQuery, GetEnrollableSessionsQueryVariables>
        query={getEnrollableSessionsQuery}
        skip={enrollableCourses?.length !== 1}
        variables={{
          courseId: enrollableCourses?.[0]?.id || '',
          userId: user.get().id.toString(),
        }}
      >
        {({ data }) => {
          const singleEnrollableCourse =
            data?.OnDemandSessionsV1Resource?.nextEnrollableSessionsByUserAndCourse?.elements?.[0];

          return (
            <EnrollmentReminderBannerContent
              enrollableCourses={enrollableCourses}
              singleEnrollableCourse={singleEnrollableCourse}
            />
          );
        }}
      </Query>
    </React.Fragment>
  );
};

export default EnrollmentReminderBanner;
